/**
 * Document workspaces over MCP (card 4.4): the `personal/<workspaceId>` wire
 * scope. Documents only — boards, calendar and memory keep `personal` /
 * `team:<id>` (owner decision 13.10.2026: workspaces are Documents only).
 *
 * A workspace is a `docscope` record in the PERSONAL bucket, and its
 * documents live in that same bucket with `scopeId` on their root row. So:
 *
 * - `personal/<id>` is reachable exactly when the docscope exists — there is
 *   no membership question, only "does the user (still) have it". A workspace
 *   deleted on another device is refused as unavailable, never written into:
 *   a root stamped with a dead id would show under Personal.
 * - `personal` keeps two meanings on purpose. A LISTING (`documents_tree`)
 *   means the Personal workspace only, so a full read names every node once.
 *   A read or write BY ID stays bucket-level (`scopes.toDocScope`), so a
 *   document id an agent already holds keeps working after the user moved it
 *   to another workspace.
 * - A document's resource URI keeps the bucket form `personal`, so it does not
 *   change when the document moves between workspaces.
 */
import { listScopes as listDocScopes } from '../extensions/bundled/documents/documentsScopes';
import { PERSONAL, WORKSPACE_PREFIX, listScopes, resolveScopes, toDocScope, workspaceIdOf } from './scopes';
import { refuseUnwritableScope, scopeReport } from './writeScope';
import { mcpError } from './errors';

export const isWorkspaceScope = (wire) => workspaceIdOf(wire) != null;

/** The document tools that take a `personal/<id>` scope; every other tool refuses it. */
export const WORKSPACE_TOOLS = Object.freeze(
  new Set([
    'documents_tree',
    'document_read',
    'document_subpages',
    'document_create_folder',
    'document_create',
    'document_rename',
    'document_write',
    'document_move',
    'document_delete',
  ])
);

/** `null` when `tool` may take `scope`, else the refusal (nothing was read). */
export function refuseWorkspaceScopeFor(tool, scope) {
  if (!isWorkspaceScope(scope) || WORKSPACE_TOOLS.has(tool)) return null;
  return mcpError(
    'invalid_argument',
    'Workspaces (personal/<workspaceId>) hold documents only. Use personal or team:<teamId> for this tool.',
    { field: 'scope' }
  );
}

/**
 * The user's workspaces as `{ ok, scopes: [{ scope, label }] }`. `ok` false
 * means the docscope read failed: the caller reports `personal/*` as
 * unavailable rather than pretending there are none.
 */
export async function listWorkspaceScopes() {
  try {
    const rows = await listDocScopes();
    return {
      ok: true,
      scopes: (rows || []).map((w) => ({ scope: `${WORKSPACE_PREFIX}${w.id}`, label: w.name || 'Workspace' })),
    };
  } catch {
    return { ok: false, scopes: [] };
  }
}

/** `scopes_list`: Personal, the user's workspaces (picker order), then the teams. */
export async function listAllScopes() {
  const [personal, ...teams] = listScopes();
  const { scopes: workspaces } = await listWorkspaceScopes();
  return [personal, ...workspaces, ...teams];
}

/** The descriptor a LISTING uses: plain `personal` is the Personal workspace only. */
export function toListScope(wire) {
  if (!wire || wire === PERSONAL) return { type: 'personal', scopeId: null };
  return toDocScope(wire);
}

/** The scope a resource URI names: a workspace document keeps the bucket form. */
export const uriScopeOf = (wire) => (isWorkspaceScope(wire) ? PERSONAL : wire || PERSONAL);

/** `{ name }` of the workspace a `personal/<id>` names, or null when there is none. */
export async function findWorkspace(wire) {
  const id = workspaceIdOf(wire);
  if (!id) return null;
  const { ok, scopes } = await listWorkspaceScopes();
  if (!ok) return null;
  const hit = scopes.find((s) => s.scope === `${WORKSPACE_PREFIX}${id}`);
  return hit ? { id, name: hit.label } : null;
}

/**
 * `resolveScopes` for the documents surface: the same answer for `personal` and
 * teams, plus the workspaces. `requested` null reads Personal, every workspace
 * and every team.
 */
export async function resolveDocScopes(requested) {
  if (isWorkspaceScope(requested)) {
    const known = (await findWorkspace(requested)) != null;
    return {
      scopes: known ? [requested] : [],
      consulted: known ? [requested] : [],
      unavailable: known ? [] : [requested],
    };
  }
  const base = resolveScopes(requested);
  if (requested) return base;
  const { ok, scopes } = await listWorkspaceScopes();
  const wires = scopes.map((s) => s.scope);
  const [personal, ...teams] = base.scopes;
  return {
    scopes: [personal, ...wires, ...teams],
    consulted: [personal, ...wires, ...teams],
    unavailable: [...base.unavailable, ...(ok ? [] : [`${WORKSPACE_PREFIX}*`])],
  };
}

/** `refuseUnwritableScope` that also knows workspaces. */
export async function refuseUnwritableDocScope(wire) {
  if (!isWorkspaceScope(wire)) return refuseUnwritableScope(wire);
  if (await findWorkspace(wire)) return null;
  return mcpError(
    'unavailable',
    'That workspace does not exist (any more), so nothing was written. Call scopes_list and retry with a scope it names.',
    { scope: wire }
  );
}

/** `scopeReport` that also knows workspaces (call after the refusal check). */
export function docScopeReport(wire) {
  if (isWorkspaceScope(wire)) return { scopesConsulted: [wire], scopesUnavailable: [] };
  return scopeReport(wire);
}

/** The wire scope a listed personal node belongs to: its root's workspace, or Personal. */
export const wireOfPersonalNode = (node) => (node?.scopeId ? `${WORKSPACE_PREFIX}${node.scopeId}` : PERSONAL);
